import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  BASE_URL,
  getToken,
  getCurrentLanguage,
} from "../../shared/utils.js/utils";

// Get payslips
export const getPayslips = createAsyncThunk(
  "payslips/getPayslips",
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = params.language || getCurrentLanguage();
      if (!token) {
        throw new Error("No authentication token found");
      }
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.search) queryParams.append("search", params.search);
      const url = `${BASE_URL}/payslips${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get payslips!";
      return rejectWithValue(message);
    }
  },
);

// Get payslip details
export const getPayslipDetails = createAsyncThunk(
  "payslips/getPayslipDetails",
  async (payload, { rejectWithValue }) => {
    try {
      const token = getToken();
      // Handle both object and string payload
      const id = typeof payload === "object" ? payload.id : payload;
      const language =
        typeof payload === "object" ? payload.language : undefined;

      if (!id) {
        throw new Error("Payslip ID is required");
      }

      const currentLang = language || getCurrentLanguage();
      if (!token) {
        throw new Error("No authentication token found");
      }
      const response = await axios.get(`${BASE_URL}/payslips/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to get payslip details!";
      return rejectWithValue(message);
    }
  },
);

// Post payslip journal
export const postPayslipJournal = createAsyncThunk(
  "payslips/postPayslipJournal",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.post(
        `${BASE_URL}/payslips/${id}/post-journal`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );

      // Check if the response indicates failure
      if (response.data && response.data.success === false) {
        const message =
          response.data.message || "Failed to post payslip journal!";
        return rejectWithValue(message);
      }

      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to post payslip journal!";
      return rejectWithValue(message);
    }
  },
);

// Get payslips summary
export const getPayslipsSummary = createAsyncThunk(
  "payslips/getPayslipsSummary",
  async ({ year, month }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const queryParams = new URLSearchParams();
      if (year) queryParams.append("year", year);
      if (month) queryParams.append("month", month);

      const url = `${BASE_URL}/payslips/summary${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get payslips summary!";
      return rejectWithValue(message);
    }
  },
);

// Get employee payslip statement
export const getEmployeePayslipStatement = createAsyncThunk(
  "payslips/getEmployeePayslipStatement",
  async ({ employeeId, year, month }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const queryParams = new URLSearchParams();
      if (year) queryParams.append("year", year);
      if (month) queryParams.append("month", month);

      const url = `${BASE_URL}/payslips/employee/${employeeId}/statement${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "Failed to get employee payslip statement!";
      return rejectWithValue(message);
    }
  },
);

// Generate payslip
export const generatePayslip = createAsyncThunk(
  "payslips/generatePayslip",
  async (payslipData, { rejectWithValue }) => {
    try {
      const token = getToken();
      if (!token) {
        throw new Error("No authentication token found");
      }
      const currentLang = getCurrentLanguage();
      const response = await axios.post(
        `${BASE_URL}/payslips/generate`,
        payslipData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to generate payslip!";
      return rejectWithValue(message);
    }
  },
);
